import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createContact, updateContact, getContact } from '../../utils/api';
import { useApp } from '../../context/AppContext';

const TRADES = ['plumber', 'electrician', 'hvac', 'carpenter', 'painter', 'roofer', 'landscaper', 'general', 'other'];

const defaultForm = { name: '', trade: 'general', company: '', phone: '', email: '', rating: '', notes: '' };

const ContactForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchContacts } = useApp();
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const isEditing = Boolean(id);

  useEffect(() => {
    if (!isEditing) return;
    getContact(id)
      .then(({ data }) => setForm({ ...defaultForm, ...data, rating: data.rating ?? '' }))
      .catch(() => setLoadError('Contact not found.'));
  }, [id, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, rating: form.rating !== '' ? Number(form.rating) : undefined };
      if (isEditing) await updateContact(id, payload);
      else await createContact(payload);
      await fetchContacts({ force: true });
      navigate('/contacts');
    } finally {
      setSaving(false);
    }
  };

  if (loadError) return <div className="page"><p className="empty-state">{loadError}</p></div>;

  return (
    <div className="page room-form-page">
      <div className="page-header">
        <h1>{isEditing ? 'Edit Contact' : 'New Contact'}</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/contacts')}>Cancel</button>
      </div>
      <form onSubmit={handleSubmit} className="room-form">
        <fieldset className="form-section">
          <legend className="form-section-title">Info</legend>
          <div className="form-row">
            <label>Name *
              <input name="name" value={form.name} onChange={handleChange} required />
            </label>
            <label>Trade *
              <select name="trade" value={form.trade} onChange={handleChange}>
                {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label>Company
              <input name="company" value={form.company} onChange={handleChange} />
            </label>
          </div>
          <div className="form-row">
            <label>Phone
              <input name="phone" value={form.phone} onChange={handleChange} type="tel" />
            </label>
            <label>Email
              <input name="email" value={form.email} onChange={handleChange} type="email" />
            </label>
            <label>Rating (1–5)
              <select name="rating" value={form.rating} onChange={handleChange}>
                <option value="">— unrated —</option>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} star{n !== 1 ? 's' : ''}</option>)}
              </select>
            </label>
          </div>
          <label>Notes
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} />
          </label>
        </fieldset>
        <div className="form-actions form-actions-bottom">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Contact'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/contacts')}>Cancel</button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
